// angular
import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

// libs
import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';

// C4 libs
import { BrokerService } from '@c4/core';

// common
import { Device } from './../../../common/interfaces/item.interface';
import { ItemsService } from './../../../common/services/items.service';
import { ProgrammingUtilsService as Util } from '../../../common/services/utils.service';
import { SharedProgrammingService, CommandData } from '../../../common/services/shared-programming.service';
import { Command } from '../../../common/params/interfaces/command.interface';
import { Parameter, ValueType } from '../../../common/params/interfaces/parameter.interface';
import { Agent } from './interfaces/agent.interface';

// module

const COMMAND_WHITELIST = ['SEND_NOTIFICATION:JSON', 'SEND_EMAIL'];

const NOTIFICATION_CONTROLS = [
  'control4_agent_notification',
  'control4_agent_emailnotification'
];

export type NotificationAgentType = 'push' | 'email';

const AGENT_MAP = {
  push: 'control4_agent_notification',
  email: 'control4_agent_emailnotification'
}

export interface NotificationProgrammingContext {
  agentType?: 'push' | 'email';
  agent?: Agent;
  commands?: Command[];
  command?: CommandData;
}

const INITIAL_STATE: NotificationProgrammingContext = {};

const STORE_NAME = ['PROGRAMMING', 'QUICKSTARTS', 'NOTIFICATIONS'].join(':');

const ACTIONS = {
  SET_AGENT_TYPE: `${STORE_NAME}:SET_AGENT_TYPE`,
  SET_AGENT: `${STORE_NAME}:SET_AGENT`,
  SET_COMMANDS: `${STORE_NAME}:SET_COMMANDS`,
  SET_COMMAND: `${STORE_NAME}:SET_COMMAND`,
  SET_COMMAND_VALUES: `${STORE_NAME}:SET_COMMAND_VALUES`,
  CLEAR: `${STORE_NAME}:CLEAR`,
}

const ACTION_STRINGS = Object.keys(ACTIONS).map(key => ACTIONS[key]);

export function notificationsProgrammingReducer(state: NotificationProgrammingContext = INITIAL_STATE, { type, payload }): NotificationProgrammingContext {

  // if (ACTION_STRINGS.indexOf(type) > -1) {
  //   console.info('%s called with %O', type, payload);
  // }

  switch (type) {
    case ACTIONS.SET_AGENT_TYPE:
      return Object.assign({}, state, { agentType: payload });

    case ACTIONS.SET_AGENT:
      return Object.assign({}, state, { agent: payload });

    case ACTIONS.SET_COMMANDS:
      return Object.assign({}, state, { commands: payload });

    case ACTIONS.SET_COMMAND:
      return Object.assign({}, state, { command: payload });

    case ACTIONS.SET_COMMAND_VALUES:
      let commands = [...state.commands];
      let commandIx = commands.findIndex(c => {
        return c.command === payload.command.command &&
          c.id === payload.command.id
      });

      commands[commandIx].params = [...payload.params];

      return Object.assign({}, state, { commands: commands });

    case ACTIONS.CLEAR:
      return Object.assign({}, INITIAL_STATE);

    default:
      return state;
  }
}



@Injectable()
export class QSNotificationsService {

  private _subscriptions: Subscription[] = [];

  constructor(
    private _broker: BrokerService,
    private _route: ActivatedRoute,
    private _store: Store<{ notificationsProgramming: NotificationProgrammingContext }>,
    private _shared: SharedProgrammingService,
    private _items: ItemsService
  ) {
    this._monitor();
  }

  setAgentType = (agent: NotificationAgentType) => this._store.dispatch({
    type: ACTIONS.SET_AGENT_TYPE,
    payload: agent
  });

  setActionCommand(command: Command, params: Parameter[] = []): Promise<any> {
    // Hidden params will have a value set, but
    // no _value. Set it.
    params = params.filter(p => !!p).map(p => {
      p._value = (p._value !== undefined) ? p._value : p.value;
      return p;
    });

    let agent = Util.snapshot<Agent>(this._store.select(s => s.notificationsProgramming.agent));

    let data: CommandData = {
      codeitemId: 0,
      deviceId: agent.id,
      command: command.command,
      beforeCodeItemId: 0,
      parentCodeItemId: 0,
      creator: '',
      type: 1
    };

    if (params.length > 0) {
      data.params = [];
      params.forEach((param, i) => {
        data.params.push({
          name: param.name,
          value: param._value
        })
      });
    }

    this._store.dispatch({ type: ACTIONS.SET_COMMAND, payload: command });

    return this._shared.saveAction(data);
  }

  updateParam = (evt: ValueType, param: Parameter, command: Command) => {

    let newParam: Parameter = Object.assign({}, param, {
      _value: evt.value,
      _display: evt.display
    });

    let params = [...command.params];

    let pix = params.findIndex(p => p.name === newParam.name && p.type === newParam.type);

    params[pix] = newParam;

    this.updateParams(params, command);
  }

  updateParams = (params: Parameter[], command: Command) => {
    this._store.dispatch({
      type: ACTIONS.SET_COMMAND_VALUES,
      payload: {
        params: params,
        command: command
      }
    })
  }

  private _monitor = (): void => {
    this._monitorAgentType();
    this._monitorAgent();
  }

  /**
   * Watches the agent type, and
   * populates the agent accordingly
   */
  private _monitorAgentType = (): void => {
    this._subscriptions.push(
      this._store.select(s => s.notificationsProgramming.agentType)
        .map(agentType => AGENT_MAP[agentType])
        .flatMap(control => {
          return this._items.itemsList.map(items => items.filter((item: Device) => item.control === control))
        })
        .filter(agents => agents.length < 2)
        .map(agents => agents[0] || undefined)
        .subscribe(agent => this._store.dispatch({
          type: ACTIONS.SET_AGENT,
          payload: agent
        }))
    );
  }

  /**
   * Watches the agent and populates the
   * commands accordingly
   */
  private _monitorAgent = (): void => {
    this._subscriptions.push(
      this._store.select(s => s.notificationsProgramming.agent)
        .map(agent => (!!agent && !!agent.URIs && !!agent.URIs.commands) ?
          agent.URIs.commands : undefined)
        .subscribe(uri => {
          if (!!uri) {
            this._broker.call({
              path: uri
            }).then(
              (commands: Command[]) => {
                commands = commands
                  .filter(c => COMMAND_WHITELIST.indexOf(c.command) > -1);
                this._store.dispatch({
                  type: ACTIONS.SET_COMMANDS,
                  payload: commands
                })
              },
              error => console.error('notifications.service.ts: error: %O', error)
              )
          } else {
            this._store.dispatch({
              type: ACTIONS.SET_COMMANDS,
              payload: []
            })
          }
        })
    );
  }

  ngOnDestroy() {
    this._subscriptions.forEach(sub => {
      sub.unsubscribe();
    });

    this.clear();
  }

  clear() {
    this._store.dispatch({
      type: ACTIONS.CLEAR
    });
  }
}



// WEBPACK FOOTER //
// ./~/@c4/programming/src/app/quickstarts/quickstart-notifications/services/notifications.service.ts